import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { authAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useBranding } from '../context/BrandingContext'
import { AuthShell } from './authShared'

const RULES = {
  len: v => v.length >= 8,
  case: v => /[a-z]/.test(v) && /[A-Z]/.test(v),
  num: v => /\d/.test(v),
  sym: v => /[^A-Za-z0-9]/.test(v),
}
const REQS = [
  { key: 'len', label: '8+ characters' },
  { key: 'case', label: 'Upper & lowercase' },
  { key: 'num', label: 'A number' },
  { key: 'sym', label: 'A symbol' },
]
const LEVELS = [
  { label: '—', color: 'var(--text-faint)' },
  { label: 'Weak', color: 'var(--danger)' },
  { label: 'Fair', color: 'var(--warning)' },
  { label: 'Good', color: 'var(--info)' },
  { label: 'Strong', color: 'var(--success)' },
]
const scorePw = v => (v ? Object.values(RULES).filter(fn => fn(v)).length : 0)

const CheckDot = () => (
  <span className="req-dot">
    <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10"/></svg>
  </span>
)
const LockIco = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
)

export default function ResetPassword() {
  const navigate = useNavigate()
  const branding = useBranding()
  const { completePasswordReset } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [step, setStep] = useState('reset') // 'reset' | 'done'
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [confirmError, setConfirmError] = useState(false)
  const [weakHint, setWeakHint] = useState(false)
  const [serverError, setServerError] = useState('')
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)

  const score = scorePw(pw)
  const level = LEVELS[score]
  const lvlLabel = weakHint && score < 3 ? 'Too weak — add more variety' : level.label
  const lvlColor = weakHint && score < 3 ? 'var(--danger)' : level.color

  async function handleSubmit(e) {
    e.preventDefault()
    setServerError('')
    if (scorePw(pw) < 3) { setWeakHint(true); return }
    if (pw !== confirm || !confirm) { setConfirmError(true); return }
    setLoading(true)
    try {
      const res = await authAPI.resetPassword(token, pw)
      // Backend revokes other sessions and hands this device a fresh session.
      if (res.data?.token && res.data?.user) {
        completePasswordReset(res.data.token, res.data.user)
        setUser(res.data.user)
      }
      setStep('done')
    } catch (err) {
      setServerError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Could not reset your password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  // ── Missing/invalid link ──
  if (!token && step !== 'done') {
    return (
      <AuthShell>
        <div className="step anim-in">
          <div className="form-medallion" style={{ background: 'var(--danger-soft)', borderColor: 'rgba(255,107,107,0.4)', color: 'var(--danger)', boxShadow: 'none' }}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.01"/></svg>
          </div>
          <div className="form-head">
            <h1>Reset link invalid</h1>
            <p>This password reset link is missing or malformed. Request a fresh one and we'll email you a new link.</p>
          </div>
          <Link to="/forgot-password" className="ds-btn ds-btn-primary btn-submit" style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
            Request a new link
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell showBack={step !== 'done'}>
      {step === 'reset' ? (
        <div className="step anim-in" key="reset">
          <div className="form-medallion">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><path d="M12 14.5v2.5"/></svg>
          </div>
          <div className="form-head">
            <h1>Set a new password</h1>
            <p>Choose a strong password for your {branding.cmsName} account. You'll use it the next time you sign in.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate style={{ marginTop: 26 }}>
            {serverError && <div className="auth-banner">{serverError}</div>}

            <div className="field">
              <div className="field-label"><label htmlFor="rp-new">New password</label></div>
              <div className="input-wrap">
                <span className="lead-ico"><LockIco /></span>
                <input
                  className="ds-input with-ico with-trail"
                  type={showPw ? 'text' : 'password'}
                  id="rp-new"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  autoFocus
                  value={pw}
                  onChange={e => { setPw(e.target.value); setWeakHint(false) }}
                />
                <button className="toggle-pw" type="button" title="Show password" aria-label="Show password" onClick={() => setShowPw(s => !s)}>
                  {showPw
                    ? <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18M10.6 6.2A9.6 9.6 0 0 1 12 5.5c6.5 0 10 6.5 10 6.5a16 16 0 0 1-3.2 3.9M6.2 7.6A16 16 0 0 0 2 12s3.5 6.5 10 6.5a9.4 9.4 0 0 0 3.6-.7M9.5 9.5a3 3 0 0 0 4.2 4.3"/></svg>
                    : <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z"/><circle cx="12" cy="12" r="2.8"/></svg>}
                </button>
              </div>
            </div>

            <div className="strength" data-score={score} style={{ '--lvl-color': level.color }}>
              <div className="strength-bars"><i /><i /><i /><i /></div>
              <div className="strength-meta">
                <span>Password strength</span>
                <span className="lvl" style={{ color: lvlColor }}>{lvlLabel}</span>
              </div>
            </div>

            <div className="reqs">
              {REQS.map(r => (
                <div key={r.key} className={`req${pw && RULES[r.key](pw) ? ' met' : ''}`}>
                  <CheckDot />{r.label}
                </div>
              ))}
            </div>

            <div className={`field${confirmError ? ' has-error' : ''}`}>
              <div className="field-label"><label htmlFor="rp-confirm">Confirm password</label></div>
              <div className="input-wrap">
                <span className="lead-ico"><LockIco /></span>
                <input
                  className="ds-input with-ico"
                  type={showPw ? 'text' : 'password'}
                  id="rp-confirm"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); if (confirmError) setConfirmError(false) }}
                />
              </div>
              <div className="field-error">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.01"/></svg>
                <span>Passwords don't match.</span>
              </div>
            </div>

            <button className="ds-btn ds-btn-primary btn-submit" type="submit" disabled={loading} style={{ opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Updating password…' : 'Reset password & sign in'}
              {!loading && <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>}
            </button>
          </form>
        </div>
      ) : (
        <div className="step anim-in" key="done">
          <div className="form-medallion is-success">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          </div>
          <div className="form-head">
            <h1>Password updated</h1>
            <p>Your password has been changed and all other active sessions were signed out. You're ready to get back to building.</p>
          </div>

          <button
            className="ds-btn ds-btn-primary btn-submit"
            type="button"
            style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => navigate(user ? (user.role === 'admin' ? '/admin' : '/client') : '/login', { replace: true })}
          >
            Continue to sign in
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </button>

          <div className="legal">
            Didn't request this change? <Link to="/login">Contact support</Link> right away.
          </div>
        </div>
      )}
    </AuthShell>
  )
}
